// ===================================================================
// HMGP — Dashboard
// Funding pool overview, subapplication pipeline, project stats
// ===================================================================

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Text, Title, Group, Stack, Badge, Button, SimpleGrid, Loader, Center,
  Progress, TextInput, ActionIcon, Tooltip,
} from '../../../components/gab/GabComponents';
import {
  IconPlus, IconArrowRight, IconAlertCircle, IconEdit, IconCheck,
} from '@tabler/icons-react';
import { usePAGrantsStore } from '../../../store/paGrantsStore';
import { useHMGPStore } from '../../../store/hmgpStore';
import { useActivePADisaster as useActiveDisaster } from '../../../hooks/useActiveDisaster';
import {
  HMGP_PROJECT_TYPE_LABELS, HMGP_PROJECT_TYPE_COLORS,
  HMGP_PHASE_LABELS, HMGP_PHASE_COLORS,
  HMGP_SUBAPP_STATUS_LABELS,
  type HMGPProjectType, type HMGPPhase,
} from '../../../types/pagrants';
import { WORKER_URL } from '../../../lib/api-switch';

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export default function HMGPDashboard() {
  const navigate = useNavigate();
  const activeDisaster = useActiveDisaster();
  const fetchDisasters = usePAGrantsStore(s => s.fetchDisasters);

  const {
    projects, subapplications, loading,
    fetchProjects, fetchSubapplications,
  } = useHMGPStore();

  // Filter by active disaster
  const disasterId = activeDisaster ? String(activeDisaster.Rid) : '';

  // Funding pool allocation (stored in KV per disaster)
  const [hmgpAllocation, setHmgpAllocation] = useState(0);
  const [editingAllocation, setEditingAllocation] = useState(false);
  const [allocationInput, setAllocationInput] = useState('');

  useEffect(() => { fetchDisasters(); }, []);
  useEffect(() => { fetchProjects(); fetchSubapplications(); }, []);

  // Load HMGP allocation from KV config
  useEffect(() => {
    if (!disasterId) return;
    fetch(`${WORKER_URL}/api/config/hmgp-allocation-${disasterId}`, { headers: { 'Origin': 'https://apps.opengov.com' } })
      .then(r => r.ok ? r.json() : 0)
      .then(v => { const n = parseFloat(v) || 0; setHmgpAllocation(n); setAllocationInput(n ? String(n) : ''); })
      .catch(() => {});
  }, [disasterId]);

  async function saveAllocation() {
    const val = parseFloat(allocationInput) || 0;
    await fetch(`${WORKER_URL}/api/config/hmgp-allocation-${disasterId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Origin': 'https://apps.opengov.com' },
      body: JSON.stringify(val),
    });
    setHmgpAllocation(val);
    setEditingAllocation(false);
  }
  const filteredProjects = useMemo(() =>
    disasterId ? projects.filter(p => p.disasterId === disasterId) : [],
    [projects, disasterId]
  );
  const filteredSubapps = useMemo(() =>
    disasterId ? subapplications.filter(s => s.disasterId === disasterId) : [],
    [subapplications, disasterId]
  );

  // ── Stats ───────────────────────────────────────────────────
  const totalProjects = filteredProjects.length;
  const totalSubapps = filteredSubapps.length;
  const totalEstimatedCost = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.estimatedCost) || 0), 0);
  const totalAwarded = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.awardAmount) || 0), 0);
  const totalDrawn = filteredProjects.reduce((sum, p) => sum + (parseFloat(p.drawdownAmount) || 0), 0);
  const pendingSubapps = filteredSubapps.filter(s => ['draft', 'submitted', 'under_review'].includes(s.status));
  const prioritizedSubapps = filteredSubapps.filter(s => s.status === 'prioritized' || s.status === 'forwarded_to_fema');

  // Phase distribution
  const phaseCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of filteredProjects) counts[p.phase] = (counts[p.phase] || 0) + 1;
    return counts;
  }, [filteredProjects]);

  // Project type distribution
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const p of filteredProjects) {
      const t = p.projectType || 'other';
      counts[t] = (counts[t] || 0) + 1;
    }
    return counts;
  }, [filteredProjects]);

  if (loading) return <Center h={300}><Loader /></Center>;

  // ── No disaster selected ────────────────────────────────────
  if (!activeDisaster) {
    return (
      <Stack gap="md">
        <Group justify="space-between">
          <div>
            <Title order={3}>Hazard Mitigation Grant Program</Title>
            <Text size="sm" c="dimmed">FEMA HMGP (Section 404)</Text>
          </div>
        </Group>
        <Paper p="lg" radius="md" style={{ background: '#f1f3f5', textAlign: 'center' }}>
          <Text size="lg" fw={600} c="dimmed" mb="sm">No Active Disaster</Text>
          <Text size="sm" c="dimmed" mb="md">Select an incident from the top bar, then link a disaster declaration to begin HMGP tracking.</Text>
          <Button onClick={() => navigate('/pagrants/hmgp/disasters')}>Manage Disasters</Button>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={3}>Hazard Mitigation Grant Program</Title>
          <Text size="sm" c="dimmed">
            {activeDisaster.disasterName} ({activeDisaster.disasterNumber})
          </Text>
        </div>
        <Group gap="xs">
          <Button size="xs" variant="light" color="green" leftSection={<IconPlus size={14} />}
            onClick={() => navigate('/pagrants/hmgp/subapplications')}>
            New Subapplication
          </Button>
        </Group>
      </Group>

      {/* ── Stat Cards ────────────────────────────────────────── */}
      <SimpleGrid cols={{ base: 2, sm: 5 }} spacing="sm">
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <Text size="xs" c="dimmed">Subapplications</Text>
          <Text size="xl" fw={700}>{totalSubapps}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #0284C7' }}>
          <Text size="xs" c="dimmed">Active Projects</Text>
          <Text size="xl" fw={700}>{totalProjects}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #7C3AED' }}>
          <Text size="xs" c="dimmed">Total Estimated</Text>
          <Text size="xl" fw={700}>{fmt(totalEstimatedCost)}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #059669' }}>
          <Text size="xs" c="dimmed">Awarded</Text>
          <Text size="xl" fw={700}>{fmt(totalAwarded)}</Text>
        </Paper>
        <Paper p="sm" radius="md" withBorder ta="center" style={{ borderTop: '3px solid #D97706' }}>
          <Text size="xs" c="dimmed">Drawn Down</Text>
          <Text size="xl" fw={700}>{fmt(totalDrawn)}</Text>
        </Paper>
      </SimpleGrid>

      {/* ── Funding Pool ─────────────────────────────────────── */}
      {hmgpAllocation > 0 ? (() => {
        const committed = totalEstimatedCost;
        const remaining = Math.max(0, hmgpAllocation - committed);
        const usedPct = hmgpAllocation > 0 ? Math.min(100, (committed / hmgpAllocation) * 100) : 0;
        const awardedPct = hmgpAllocation > 0 ? Math.min(100, (totalAwarded / hmgpAllocation) * 100) : 0;
        return (
          <Paper withBorder radius="md" p="md">
            <Group justify="space-between" mb="xs">
              <Group gap="xs">
                <Text size="sm" fw={600}>HMGP Funding Pool</Text>
                <Badge variant="light" color="green" size="sm">
                  {fmt(hmgpAllocation)} Total Allocation
                </Badge>
              </Group>
              <Tooltip label="Edit allocation">
                <ActionIcon variant="subtle" size="sm" onClick={() => { setEditingAllocation(true); setAllocationInput(String(hmgpAllocation)); }}>
                  <IconEdit size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
            <Progress.Root size="xl" radius="md" mb="xs">
              <Progress.Section value={awardedPct} color="green">
                <Progress.Label>Awarded</Progress.Label>
              </Progress.Section>
              <Progress.Section value={usedPct - awardedPct > 0 ? usedPct - awardedPct : 0} color="blue">
                <Progress.Label>Committed</Progress.Label>
              </Progress.Section>
            </Progress.Root>
            <Group justify="space-between">
              <Group gap="lg">
                <div><Text size="xs" c="dimmed">Committed</Text><Text size="sm" fw={600}>{fmt(committed)}</Text></div>
                <div><Text size="xs" c="dimmed">Awarded</Text><Text size="sm" fw={600} c="green">{fmt(totalAwarded)}</Text></div>
                <div><Text size="xs" c="dimmed">Drawn Down</Text><Text size="sm" fw={600} c="orange">{fmt(totalDrawn)}</Text></div>
              </Group>
              <div style={{ textAlign: 'right' }}>
                <Text size="xs" c="dimmed">Remaining</Text>
                <Text size="sm" fw={700} c={remaining > 0 ? 'green' : 'red'}>{fmt(remaining)}</Text>
              </div>
            </Group>
          </Paper>
        );
      })() : (
        <Paper withBorder radius="md" p="md" ta="center">
          <Text size="sm" c="dimmed" mb="xs">No HMGP funding allocation set for this disaster.</Text>
          {!editingAllocation ? (
            <Button size="xs" variant="light" color="green" onClick={() => setEditingAllocation(true)}>
              Set HMGP Allocation
            </Button>
          ) : null}
        </Paper>
      )}

      {/* Allocation edit inline */}
      {editingAllocation && (
        <Paper withBorder radius="md" p="sm">
          <Group gap="xs">
            <Text size="sm" fw={500}>HMGP Allocation ($):</Text>
            <TextInput size="xs" w={200} placeholder="e.g., 15000000"
              value={allocationInput} onChange={e => setAllocationInput(e.target.value)} />
            <Button size="xs" color="green" leftSection={<IconCheck size={14} />} onClick={saveAllocation}>Save</Button>
            <Button size="xs" variant="light" onClick={() => setEditingAllocation(false)}>Cancel</Button>
          </Group>
          <Text size="xs" c="dimmed" mt={4}>HMGP is typically funded at 15% of total estimated PA disaster costs.</Text>
        </Paper>
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        {/* ── Subapplication Pipeline ──────────────────���──────── */}
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>Subapplication Pipeline</Text>
            <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/pagrants/hmgp/subapplications')}>
              View All
            </Button>
          </Group>
          {filteredSubapps.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No subapplications yet. Create one to begin the HMGP process.
            </Text>
          ) : (
            <Stack gap="xs">
              {['draft', 'submitted', 'under_review', 'prioritized', 'forwarded_to_fema', 'returned'].map(status => {
                const count = filteredSubapps.filter(s => s.status === status).length;
                if (count === 0) return null;
                const label = HMGP_SUBAPP_STATUS_LABELS[status as keyof typeof HMGP_SUBAPP_STATUS_LABELS] || status;
                const color = status === 'forwarded_to_fema' ? 'green' : status === 'prioritized' ? 'teal' : status === 'returned' ? 'orange' : 'blue';
                return (
                  <Group key={status} justify="space-between">
                    <Badge variant="light" color={color}>{label}</Badge>
                    <Text size="sm" fw={600}>{count}</Text>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Paper>

        {/* ── Project Phase Pipeline ──────────────────────────── */}
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="sm">
            <Text size="sm" fw={600}>Project Pipeline</Text>
            <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/pagrants/hmgp/projects')}>
              View All
            </Button>
          </Group>
          {totalProjects === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">
              No HMGP projects yet. Projects are created when subapplications are approved.
            </Text>
          ) : (
            <Stack gap="xs">
              {Object.entries(HMGP_PHASE_LABELS).map(([phase, label]) => {
                const count = phaseCounts[phase] || 0;
                if (count === 0) return null;
                const color = HMGP_PHASE_COLORS[phase as HMGPPhase] || 'gray';
                return (
                  <Group key={phase} justify="space-between">
                    <Badge variant="light" color={color}>{label}</Badge>
                    <Text size="sm" fw={600}>{count}</Text>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Paper>
      </SimpleGrid>

      {/* ── Project Type Breakdown ────────────────────────────── */}
      {totalProjects > 0 && (
        <Paper withBorder radius="md" p="md">
          <Text size="sm" fw={600} mb="sm">Projects by Mitigation Type</Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            {Object.entries(typeCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([type, count]) => (
                <Group key={type} gap="xs">
                  <div style={{
                    width: 10, height: 10, borderRadius: 2,
                    backgroundColor: HMGP_PROJECT_TYPE_COLORS[type as HMGPProjectType] || '#6B7280',
                  }} />
                  <Text size="sm">
                    {HMGP_PROJECT_TYPE_LABELS[type as HMGPProjectType] || type}
                  </Text>
                  <Text size="sm" fw={600} c="dimmed">{count}</Text>
                </Group>
              ))}
          </SimpleGrid>
        </Paper>
      )}

      {/* ── Action Items ──────────────────────────────────────── */}
      {(pendingSubapps.length > 0 || prioritizedSubapps.length > 0) && (
        <Paper withBorder radius="md" p="md">
          <Text size="sm" fw={600} mb="sm">Action Items</Text>
          <Stack gap="xs">
            {pendingSubapps.length > 0 && (
              <Group justify="space-between">
                <Group gap="xs">
                  <IconAlertCircle size={16} color="#D97706" />
                  <Text size="sm">{pendingSubapps.length} subapplication{pendingSubapps.length > 1 ? 's' : ''} pending review</Text>
                </Group>
                <Button variant="subtle" size="xs" onClick={() => navigate('/pagrants/hmgp/subapplications')}>
                  Review
                </Button>
              </Group>
            )}
            {prioritizedSubapps.length > 0 && (
              <Group justify="space-between">
                <Group gap="xs">
                  <IconAlertCircle size={16} color="#059669" />
                  <Text size="sm">{prioritizedSubapps.length} subapplication{prioritizedSubapps.length > 1 ? 's' : ''} ready for FEMA submission</Text>
                </Group>
                <Button variant="subtle" size="xs" onClick={() => navigate('/pagrants/hmgp/subapplications')}>
                  View
                </Button>
              </Group>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
